import { useMemo, useState } from "react";
import Markdown from "react-markdown";
import { useLocation, useNavigate } from "react-router-dom";
import { useCopyToClipboard } from "react-use";
import { Virtuoso } from "react-virtuoso";
import Close from "@mui/icons-material/Close";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import InsertDriveFileOutlined from "@mui/icons-material/InsertDriveFileOutlined";
import Link from "@mui/icons-material/Link";
import MoreHoriz from "@mui/icons-material/MoreHoriz";
import {
  Box,
  Button,
  CircularProgress,
  Fade,
  IconButton,
  Menu,
  MenuItem,
  menuItemClasses,
  Modal,
  Stack,
  styled,
  Tooltip,
  Typography as T,
} from "@mui/material";
import { TypographyProps } from "@mui/material/Typography/Typography";
import DeleteFileDialog from "files/components/DeleteFileDialog";
import FileActionDialog from "files/components/FileActionDialog";
import FileDownload from "files/components/FileDownload";
import FileTitle from "files/components/FileTitle";
import TablePreview from "files/components/TablePreview";
import useFileAccess from "files/hooks/useFileAccess";
import useFilePreview, { DEFAULT_ERROR_MESSAGE } from "files/hooks/useFilePreview";
import useFileTitle from "files/hooks/useFileTitle";
import { encodeObjectKey } from "files/hooks/useFiles";
import FileInfo from "files/types/interfaces/fileInfo";
import { JobType } from "files/types/interfaces/fileJob";
import prettyBytes from "pretty-bytes";
import { Highlight } from "prism-react-renderer";
import remarkGfm from "remark-gfm";
import { HEADER_HEIGHT } from "~/components/AppHeader";
import ErrorBoundary from "~/components/ErrorBoundary";
import PDFViewer from "~/components/PDFViewer";
import StickyToolbar from "~/components/StickyToolbar";
import TextOverflow from "~/components/TextOverflow";
import usePrismLanguages, { PrismLanguage } from "~/hooks/usePrismLanguages";
import { codeTheme } from "~/lib/codeTheme";

// Not all PrismJS language grammars match a file's extension
const EXTENSION_TO_GRAMMAR: Record<string, PrismLanguage> = {
  sh: "bash",
  py: "python",
  yml: "yaml",
};

interface PreviewRendererProps {
  fileData: string;
  fileKey?: string;
}

const LoadingProgress = (props: { progress?: number; fileSize?: number }) => {
  const formattedSize = prettyBytes(props.fileSize ?? 0);
  const formattedPercent = `${props.progress?.toFixed(0)}%`;
  const labelProps: TypographyProps = {
    variant: "body2",
    sx: { position: "absolute", color: "grey.300", width: "100%", textAlign: "center" },
  };

  return (
    <Fade in>
      <Stack
        position="absolute"
        justifyContent="center"
        alignItems="center"
        width="100%"
        height="100%"
        flex={1}
        gap={1}
        p={3}
      >
        <CircularProgress
          sx={{
            color: "common.white",
            opacity: 0.6,
          }}
        />
        <Box position="relative" width="100%">
          <Fade in={!props.progress} timeout={{ exit: 500 }}>
            <T {...labelProps}>Loading...</T>
          </Fade>
          <Fade in={!!props.progress} timeout={500}>
            <T {...labelProps}>
              {formattedPercent} of {formattedSize}
            </T>
          </Fade>
        </Box>
      </Stack>
    </Fade>
  );
};

const ErrorMessage = (props: { message: string; fileKey: string }) => (
  <Stack
    sx={{
      alignItems: "center",
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      gap: 2,
    }}
  >
    <T variant="body2" color="common.white">
      {props.message}
    </T>
    <FileDownload
      fileKey={props.fileKey}
      callToAction={<Button variant="contained">Download</Button>}
    />
  </Stack>
);

const LineNumber = styled("span")(({ theme }) => ({
  fontFamily: "Menlo",
  userSelect: "none",
  boxSizing: "content-box",
  paddingRight: theme.spacing(1),
  paddingLeft: theme.spacing(1),
  display: "inline-block",
  textAlign: "end",
  height: "100%",
  borderRight: `1px solid ${theme.palette.grey[300]}`,
  color: theme.palette.text.graphite,
  backgroundColor: theme.palette.grey[100],
  fontSize: 14,
  opacity: 0.8,
}));

/**
 * Helper function to correct links used in html when rendered inside an iFrame using srcdoc
 *
 * note: This would certainly be easier to do using DOM APIs to convert the string to real elements and edit attributes directly instead of RegExp,
 * but this would require us to sanitize the html first (to avoid XSS), causing JavaScript that we want to run in the sandboxed iFrame later on to get removed.
 * @param html - Original markup document content
 */
const fixIframeLinks = (html: string): string => {
  return html.replace(/href=(["'])#/g, "href=$1about:srcdoc#");
};

const TextPreviewRenderer = ({ fileData, fileKey }: Required<PreviewRendererProps>) => {
  const extension = fileKey.split(".").at(-1)?.toLowerCase() ?? "";
  const language = EXTENSION_TO_GRAMMAR[extension] ?? extension;
  const { Prism: PrismInstance } = usePrismLanguages([language]);
  const renderAsMarkup = ["docx", "htm", "html"].includes(extension);
  const renderAsTable = ["csv", "tsv", "xls", "xlsx"].includes(extension);

  if (extension === "md") {
    return (
      <Box height="fit-content" mx={2} width="100%" maxWidth={1400}>
        <Box
          px={3}
          py={1}
          minHeight={300}
          bgcolor="common.white"
          borderRadius={1}
          className="prose prose-sm max-w-none"
        >
          <Markdown remarkPlugins={[remarkGfm]}>{fileData}</Markdown>
        </Box>
      </Box>
    );
  }

  if (renderAsTable) {
    return <TablePreview fileData={fileData} />;
  }

  if (renderAsMarkup) {
    return (
      <Box
        component="iframe"
        srcDoc={fixIframeLinks(fileData)}
        // caution: Do NOT enable `allow-same-origin` ever, or else this becomes prone to XSS
        sandbox="allow-scripts allow-popups allow-top-navigation-by-user-activation"
        bgcolor="common.white"
        width="100%"
        height="100%"
        border="none"
      />
    );
  }

  return (
    <Highlight theme={codeTheme} code={fileData} language={language} prism={PrismInstance as any}>
      {({ style, tokens, getLineProps, getTokenProps }) => {
        const lineHeight = 21;
        return (
          <Box
            component="pre"
            sx={{
              my: 0,
              fontSize: 13,
              flex: 1,
              mx: 2,
              borderRadius: 1,
              overflow: "hidden",
              bgcolor: "common.white",
              // Set virtual list height to remaining viewport space or height of content, whichever is less
              height: `min(100%, ${tokens.length * lineHeight}px)`,
              ...style,
            }}
          >
            <Virtuoso
              overscan={200}
              data={tokens}
              fixedItemHeight={lineHeight}
              itemContent={(index, line) => (
                <Box {...getLineProps({ line })} sx={{ height: lineHeight }}>
                  <LineNumber sx={{ width: String(tokens.length).length * 8 }}>
                    {index + 1}
                  </LineNumber>
                  <Box display="inline-block" px={1}>
                    {line.map((token, key) => (
                      <Box component="span" key={key} {...getTokenProps({ token })} />
                    ))}
                  </Box>
                </Box>
              )}
            />
          </Box>
        );
      }}
    </Highlight>
  );
};

const PDFRenderer = ({ fileKey, fileData }: Required<PreviewRendererProps>) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedData = useMemo(() => fileData, [fileKey]);

  return (
    <PDFViewer
      DocumentProps={{ file: memoizedData }}
      loading={<LoadingProgress />}
      fallback={<ErrorMessage message="Unable to render PDF" fileKey={fileKey} />}
    />
  );
};

const ImagePreviewRenderer = ({ fileData }: PreviewRendererProps) => {
  return <Box component="img" src={fileData} alt="" maxHeight="100%" />;
};

interface PreviewHeaderProps {
  fileKey: string;
  handleClosePreview: () => void;
  customHandleCopyLink?: () => void;
  displayMoreActions?: boolean;
}

const PreviewHeader = ({
  fileKey,
  handleClosePreview,
  customHandleCopyLink,
  displayMoreActions = true,
}: PreviewHeaderProps) => {
  const [, copy] = useCopyToClipboard();
  const { title = "" } = useFileTitle(fileKey);
  const canModify = useFileAccess(fileKey, "write");
  const [linkCopied, setLinkCopied] = useState(false);
  const [fileAction, setFileAction] = useState<JobType | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const fileId = fileKey.replace("/", "-");

  const handleCopyLink = () => {
    setLinkCopied(true);
    const url = new URL(
      `${window.location.pathname}/${encodeObjectKey(title)}?preview=1`,
      window.location.origin
    );
    copy(url.toString());
  };

  const handleDelete = () => {
    setAnchorEl(null);
    setDeleteModalOpen(true);
  };

  return (
    <>
      <StickyToolbar
        noBorder
        sx={{
          width: "100%",
          height: HEADER_HEIGHT,
          bgcolor: "transparent",
          color: "common.white",
          justifyContent: "space-between",
          px: 1.5,
        }}
      >
        <T
          variant="inherit"
          component="h1"
          sx={{
            fontSize: "1rem",
            fontWeight: 600,
            display: "flex",
            gap: 1,
            minWidth: 0,
            alignItems: "center",
            pointerEvents: "auto",
          }}
        >
          <InsertDriveFileOutlined fontSize="small" />
          <TextOverflow>
            <FileTitle fileKey={fileKey} />
          </TextOverflow>
        </T>
        <Box sx={{ pointerEvents: "auto", display: "flex", gap: 1 }}>
          <FileDownload
            key="download"
            fileKey={fileKey}
            callToAction={
              <Tooltip title="Download file">
                <IconButton sx={{ color: "inherit" }} size="small">
                  <FileDownloadOutlined />
                </IconButton>
              </Tooltip>
            }
          />
          <Tooltip
            title={linkCopied ? "Copied!" : "Copy Link"}
            TransitionProps={{
              onExited: () => {
                if (linkCopied) {
                  setLinkCopied(false);
                }
              },
            }}
          >
            <IconButton
              sx={{ color: "inherit" }}
              size="small"
              onClick={customHandleCopyLink ?? handleCopyLink}
            >
              <Link />
            </IconButton>
          </Tooltip>
          {displayMoreActions && (
            <>
              <IconButton
                id={`action-btn-${fileKey}`}
                aria-haspopup="true"
                aria-expanded={anchorEl ? "true" : undefined}
                aria-controls={anchorEl ? `action-menu-${fileId}` : undefined}
                onClick={(e) => setAnchorEl(e.currentTarget)}
                size="small"
                sx={{
                  color: "inherit",
                }}
              >
                <MoreHoriz />
              </IconButton>
              <Menu
                open={!!anchorEl}
                anchorEl={anchorEl}
                onClose={(e: Event) => {
                  e.stopPropagation();
                  setAnchorEl(null);
                }}
                MenuListProps={{
                  "aria-labelledby": `action-btn-${fileId}`,
                }}
                sx={{ mt: 1 }}
              >
                <MenuItem
                  onClick={() => {
                    setAnchorEl(null);
                    setFileAction(JobType.COPY);
                  }}
                >
                  Copy File...
                </MenuItem>
                {canModify && (
                  <MenuItem
                    onClick={() => {
                      setAnchorEl(null);
                      setFileAction(JobType.RENAME);
                    }}
                  >
                    Rename...
                  </MenuItem>
                )}
                {canModify && (
                  <MenuItem
                    onClick={() => {
                      setAnchorEl(null);
                      setFileAction(JobType.MOVE);
                    }}
                  >
                    Move...
                  </MenuItem>
                )}
                {canModify && (
                  <MenuItem
                    onClick={handleDelete}
                    sx={{
                      color: "attention.main",
                      [`&.${menuItemClasses.root}:hover`]: {
                        color: "common.white",
                        bgcolor: "attention.main",
                      },
                    }}
                  >
                    Delete
                  </MenuItem>
                )}
              </Menu>
            </>
          )}

          <IconButton sx={{ color: "inherit", mr: -0.5 }} size="small" onClick={handleClosePreview}>
            <Close />
          </IconButton>
        </Box>
      </StickyToolbar>
      {fileAction && (
        <FileActionDialog
          fileKey={fileKey}
          open={!!fileAction}
          action={fileAction}
          isDirectory={false}
          handleClose={() => {
            setFileAction(null);
          }}
        />
      )}

      <DeleteFileDialog
        fileKey={fileKey}
        open={deleteModalOpen}
        isDirectory={false}
        handleClose={(deleted) => {
          setDeleteModalOpen(false);
          if (deleted) {
            handleClosePreview();
          }
        }}
      />
    </>
  );
};

export const FilePreviewContent = ({ file }: { file: Pick<FileInfo, "key" | "size"> }) => {
  const { data, progress, loading: previewDataLoading, error } = useFilePreview(file);
  const showPreviewRenderer = data?.content;
  const showErrorMessage = !previewDataLoading && (!data?.content || error);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        position: "relative",
      }}
    >
      {previewDataLoading && <LoadingProgress progress={progress} fileSize={file?.size} />}
      {showPreviewRenderer && (
        <ErrorBoundary
          fallback={<ErrorMessage message={error || DEFAULT_ERROR_MESSAGE} fileKey={file.key} />}
        >
          {data.type === "text" && (
            <TextPreviewRenderer fileData={data.content} fileKey={file.key} />
          )}
          {data.type === "pdf" && <PDFRenderer fileData={data.content} fileKey={file.key} />}
          {data.type === "image" && <ImagePreviewRenderer fileData={data.content} />}
        </ErrorBoundary>
      )}
      {showErrorMessage && (
        <ErrorMessage message={error || DEFAULT_ERROR_MESSAGE} fileKey={file?.key} />
      )}
    </Box>
  );
};

interface FilePreviewProps {
  file: Pick<FileInfo, "key" | "size">;
  onClose?: () => void;
  handleCopyLink?: () => void;
  displayMoreActions?: boolean;
}

const FilePreview = ({ file, onClose, handleCopyLink, displayMoreActions }: FilePreviewProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      navigate({ pathname: ".", search: location.search }, { relative: "path", replace: true });
    }
  };

  return (
    <Modal
      open
      disableEscapeKeyDown
      onClose={handleClose}
      slotProps={{
        backdrop: {
          sx: { bgcolor: "rgba(0, 0, 0, 0.7)", backdropFilter: "blur(1px)" },
        },
      }}
    >
      <Stack
        sx={{
          width: "100%",
          height: "100%",
          minHeight: "100vh",
          alignItems: "center",
          outline: "none",
          // Allow clicks on the backdrop
          pointerEvents: "none",
        }}
      >
        <PreviewHeader
          fileKey={file.key}
          handleClosePreview={handleClose}
          customHandleCopyLink={handleCopyLink}
          displayMoreActions={displayMoreActions}
        />
        <Box
          sx={{
            width: "100%",
            minHeight: `calc(100vh - ${HEADER_HEIGHT})`,
            overflow: "auto",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
            "> *": {
              pointerEvents: "auto",
            },
          }}
        >
          <FilePreviewContent file={file} />
        </Box>
      </Stack>
    </Modal>
  );
};

export default FilePreview;
